import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Row, Col, Table, Button, Spinner, Alert, Modal, InputGroup } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { adminApi, extractError } from '../api/client';
import { fmtRelative } from '../lib/lookups';
import { useToast } from '../contexts/ToastContext';

function StatusPill({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`status-badge ${isActive ? 'status-published' : 'status-archived'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

export default function CountiesListPage() {
  const { stateId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/states/${stateId}`);
        if (active) setState(res.data);
      } catch (e) {
        if (active) setError(extractError(e));
      }
    })();
    return () => { active = false; };
  }, [stateId]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      const res = await adminApi.get(`/admin/states/${stateId}/counties`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId, page, q, status]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/states/${stateId}/counties/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <PageHeader
        title={state ? `${state.name} Counties` : 'Counties'}
        subtitle={state ? `${total} ${total === 1 ? 'county' : 'counties'} in ${state.name} (${state.abbreviation})` : '—'}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {state && <> / <Link to={`/admin/states/${stateId}`} className="text-decoration-none">{state.name}</Link></>}
            {' / '} Counties
          </span>
        }
        actions={
          <Button variant="primary" onClick={() => navigate(`/admin/states/${stateId}/counties/new`)} data-testid="counties-create-button">
            <i className="bi bi-plus-lg me-1" /> New county
          </Button>
        }
      />
      <div className="page-content">
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col xs={12} md={6}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control
                    placeholder="Name, slug, code…"
                    value={q}
                    onChange={(e) => { setPage(1); setQ(e.target.value); }}
                    data-testid="counties-search-input"
                  />
                </InputGroup>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Status</Form.Label>
                <Form.Select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="counties-status-select">
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-pin-map" />
              <div>No counties for this state yet.</div>
              <Button variant="primary" className="mt-2" onClick={() => navigate(`/admin/states/${stateId}/counties/new`)}>Add first county</Button>
            </div>
          ) : (
            <>
              <Table hover responsive size="sm" className="mb-0" data-testid="counties-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} data-testid={`county-row-${c.id}`}>
                      <td>
                        <Link to={`/admin/states/${stateId}/counties/${c.id}`} className="text-decoration-none fw-semibold" data-testid={`county-name-link-${c.id}`}>
                          {c.name}
                        </Link>
                      </td>
                      <td><code className="token-mono">{c.county_code || '—'}</code></td>
                      <td className="text-muted" style={{ fontSize: '0.85rem' }}>{c.slug}</td>
                      <td><StatusPill status={c.status} /></td>
                      <td className="text-muted">{fmtRelative(c.updated_at)}</td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <Link to={`/admin/states/${stateId}/counties/${c.id}`}><Button size="sm" variant="outline-primary" data-testid={`county-edit-${c.id}`}>Edit</Button></Link>
                          <Button size="sm" variant="outline-danger" onClick={() => setDeleteTarget(c)} data-testid={`county-delete-${c.id}`}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>Page {page} of {totalPages} · {total} counties</div>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton><Modal.Title>Delete county</Modal.Title></Modal.Header>
        <Modal.Body>Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-county-button">
            {deleting ? <Spinner size="sm" animation="border" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
